import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { QueryFilters } from 'react-query/types/core/utils';
import { SchemaObject, SchemaService } from '@frontend/schema';
import { SchemaQueryKey } from '../use-schema-query';
import { getSchemaEndpointQueryFilters } from './get-schema-endpoint-query-filters';
import { SchemaQueryData, SchemaQueryFilters } from './types';

type GetSchemaQueriesDataArgs<
  Service extends SchemaObject<SchemaService>,
  EndpointName extends keyof Service,
  Infinite extends boolean = false
> = {
  endpointName: EndpointName;
  queryFilters?: SchemaQueryFilters<Service, EndpointName, Infinite>;
};

/**
 * A hook that returns a function to retrieve the data of all queries that match the given filters for a schema endpoint, with better typing.
 * @param serviceName The name of the service used in the query key.
 * @returns A function that returns the data of all queries that match the given filters for a schema endpoint.
 */
export const useGetSchemaQueriesData = <Service extends SchemaObject<SchemaService>>(serviceName: string) => {
  const queryClient = useQueryClient();

  /**
   * A function that retrieves the data of all queries that match the given filters for a schema endpoint.
   * @param endpointName The name of the endpoint in a given schema service.
   * @param queryFilters (optional) The query filters to apply to the endpoint queries, with schema-based types for the `predicate` and `queryKey` types.
   * @returns The data of all queries that match the given filters for a schema endpoint with schema-based types.
   *
   * @template EndpointName The name of the endpoint in the given schema service.
   * @template Infinite Whether the endpoint query is infinite or not.
   */
  const getQueriesData = useCallback(
    <EndpointName extends keyof Service, Infinite extends boolean = false>({
      endpointName,
      queryFilters,
    }: GetSchemaQueriesDataArgs<Service, EndpointName, Infinite>) => {
      return queryClient.getQueriesData<SchemaQueryData<Service, EndpointName, Infinite>>(
        // Our type definitions are more strict than the react-query ones, so we need to cast here
        getSchemaEndpointQueryFilters({ serviceName, endpointName, queryFilters }) as unknown as QueryFilters
        // Type the query key here as well, to avoid generic QueryKey type
      ) as [SchemaQueryKey<Service, EndpointName>, SchemaQueryData<Service, EndpointName, Infinite>][];
    },
    [queryClient, getSchemaEndpointQueryFilters]
  );

  return getQueriesData;
};
